import React from 'react';
import { cn } from './lib/utils';
import { useFlatUI } from './flat-ui';
interface CoinSpinnerProps {
  size?: number;
  src?: string;
  className?: string;
  children?: React.ReactNode;
  loading?: boolean;
  message?: string;
  fullScreen?: boolean;
}

const CoinSpinner: React.FC<CoinSpinnerProps> = ({
  size = 50,
  src = '/img/chat.png',
  className = '',
  children,
  loading = false,
  message,
  fullScreen = false,
}) => {
  const flat = useFlatUI();

  return (
    <div
      className={cn(
        fullScreen
          ? 'fixed inset-0 flex items-center justify-center z-50'
          : // min-w-0: when this wraps DialogContent children it is a grid item; without it the
            // long-filename min-content width still blows past the dialog max-width.
            'h-full w-full min-w-0'
      )}
    >
      {children}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-[2000]">
          <div
            className={`flex flex-col items-center justify-center gap-3 ${className}`}
          >
            {message && (
              <span
                className={cn(
                  'text-white text-sm font-medium px-4 py-2 rounded-lg bg-slate-800/50 backdrop-blur-sm shadow-lg whitespace-nowrap',
                  flat && 'shadow-none'
                )}
              >
                {message}
              </span>
            )}
            <img
              src={src}
              alt="Spinning coin"
              className="animate-[coinSpin_3s_linear_infinite]"
              width={size}
              height={size}
              loading="eager"
              style={{ display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Client-only, without depending on `next/dynamic`.
 *
 * The original wrapped an already-imported component in `dynamic(..., { ssr: false })` purely to
 * skip server rendering. That made a shared component require a framework API, which broke the
 * moment it was used outside a Next app. Rendering nothing until mounted is the same behaviour:
 * the server and first client pass agree on empty, so there is no hydration mismatch.
 */
const ClientOnlyCoinSpinner: React.FC<CoinSpinnerProps> = (props) => {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <CoinSpinner {...props} />;
};

export default ClientOnlyCoinSpinner;
